import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoginRequestSchema } from "@repo/shared";
import { apiFetch } from "../../lib/api.js";
import { setAccessToken } from "../../lib/auth.js";
import type { LoginResponse } from "@repo/shared";

function KsefAuthFailedModal({ onClose, onGoToIntegrations }: {
  onClose: () => void;
  onGoToIntegrations: () => void;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: 32,
          maxWidth: 440,
          width: "90%",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "#fef2f2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>Błąd autoryzacji KSeF</div>
          </div>
        </div>

        <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.6, marginBottom: 24 }}>
          Weryfikacja tokenu KSeF nie powiodła się podczas ostatniej kontroli. Faktury z KSeF
          nie będą pobierane do czasu aktualizacji tokenu.
        </p>
        <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.5, marginBottom: 24 }}>
          Przejdź do zakładki <strong>Integracje → KSeF</strong>, aby podać nowy token i zweryfikować połączenie.
        </p>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{
              padding: "9px 18px",
              background: "#fff",
              color: "#6b7280",
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Zamknij
          </button>
          <button
            onClick={onGoToIntegrations}
            style={{
              padding: "9px 18px",
              background: "#15803d",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Przejdź do integracji
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showKsefModal, setShowKsefModal] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const parsed = LoginRequestSchema.safeParse({ email, password });
    if (!parsed.success) {
      setError("Podaj poprawny adres email i hasło.");
      return;
    }

    setLoading(true);
    try {
      const res = await apiFetch<LoginResponse>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(parsed.data),
      });
      setAccessToken(res.access_token);

      try {
        const ksefStatus = await apiFetch<{ authStatus: string }>("/api/integrations/ksef");
        if (ksefStatus.authStatus === "failed") {
          setShowKsefModal(true);
          return;
        }
      } catch {
        // non-critical — proceed normally if check fails
      }

      void navigate("/debt-collection");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Błąd logowania. Spróbuj ponownie.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      {showKsefModal && (
        <KsefAuthFailedModal
          onClose={() => { setShowKsefModal(false); void navigate("/debt-collection"); }}
          onGoToIntegrations={() => { setShowKsefModal(false); void navigate("/integrations"); }}
        />
      )}

      <div className="login-card">
        <div className="login-logo">
          <div className="login-logo-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/>
              <path d="M2 17l10 5 10-5"/>
              <path d="M2 12l10 5 10-5"/>
            </svg>
          </div>
          <div>
            <div className="login-logo-name">SM Finance</div>
            <div className="login-logo-sub">Automatyzacja finansów</div>
          </div>
        </div>

        <h1 className="login-heading">Zaloguj się</h1>
        <p className="login-subheading">Wprowadź dane konta operatora</p>

        {error && (
          <div className="alert alert-error" role="alert">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {error}
          </div>
        )}

        <form onSubmit={(e) => void handleSubmit(e)}>
          <div className="form-group">
            <label className="form-label" htmlFor="email">Adres email</label>
            <input
              id="email"
              className="form-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="operator@firma.pl"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="password">Hasło</label>
            <input
              id="password"
              className="form-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="••••••••"
            />
          </div>

          <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
            {loading ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
                Logowanie…
              </>
            ) : "Zaloguj się"}
          </button>
        </form>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
